import { z } from "zod";
import type { Ref } from "react";
import { UseFormReturn } from "react-hook-form";
import { VALIDATION_MESSAGES_CARD } from "constants/validations";
import {
    firstNameSchema,
    lastNameSchema,
    optionalFirstNameSchema,
    optionalLastNameSchema,
    addressSchema,
    optionalAddressSchema,
    citySchema,
    optionalCitySchema,
    stateSchema,
    optionalStateSchema,
    zipCodeSchema,
    optionalZipCodeSchema,
    phoneSchema,
    optionalPhoneSchema,
    emailSchema,
    phoneTypeSchema,
    genderSchema,
    aaaMemberNumberSchema,
} from "components/form-fields";
import {
    dtoDateOfBirthSchema,
    mltaDateOfBirthSchema,
    giwlDateOfBirthSchema,
} from "components/form-fields/date-of-birth-field/dateOfBirthFieldSchema";
import { giwlStateSchema } from "components/form-fields/state-field/stateFieldSchema";
import { BeneficiaryRelationshipsEnum, UsStates } from "state/RootSchema";

export const ApplicantTypeEnum = z.enum(["member", "spouse"]);

export const CoverageReplacementSchema = z
    .object({
        isReplacing: z
            .boolean()
            .nullable()
            .refine((val) => val !== null, {
                message: VALIDATION_MESSAGES_CARD.SELECT_OPTION,
            }),
        replacementCompany: z.string(),
        replacementAccount: z.string(),
    })
    .refine(
        (data) => {
            // InsuranceCompany is required when replacing == true
            if (data.isReplacing === true) {
                return data.replacementCompany.trim().length > 0;
            }
            return true;
        },
        {
            message: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED,
            path: ["replacementCompany"],
        }
    );

export interface CoverageReplacementValues {
    replacementIndicator: boolean | null;
    replacementCompany?: string;
    replacementAccount?: string;
}

export interface BaseRef {
    validate: () => Promise<boolean>;
}

export type WithRef<TProps, TRef> = TProps & {
    ref?: Ref<TRef>;
};

export interface CoverageReplacementRef extends BaseRef {
    getValues: () => CoverageReplacementValues;
    shouldDisableNext: () => boolean;
}

export const StatementOfHealthSchema = z.object({
    height: z.object({
        feet: z
            .string()
            .min(1, { message: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED })
            .regex(/^[3-7]$/, { message: "Feet must be between 3 and 7" }),
        inches: z
            .string()
            .min(1, { message: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED })
            .regex(/^([0-9]|1[01])$/, { message: "Inches must be between 0 and 11" }),
    }),
    weight: z
        .string()
        .min(1, { message: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED })
        .regex(/^[0-9]{1,3}$/, { message: "Must be a valid weight" })
        .refine(
            (val) => {
                const num = parseInt(val);
                return num >= 50 && num <= 500;
            },
            { message: "Weight must be between 50 and 500 lbs" }
        ),
    hasUsedNicotine: z.boolean(), // Read-only but part of form data
    hasMedicalConditions: z
        .boolean()
        .nullable()
        .refine((val) => val !== null, {
            message: VALIDATION_MESSAGES_CARD.SELECT_OPTION,
        }),
    hasDiagnosticTesting: z
        .boolean()
        .nullable()
        .refine((val) => val !== null, {
            message: VALIDATION_MESSAGES_CARD.SELECT_OPTION,
        }),
});

export interface StatementOfHealthValues {
    heightFeet: string;
    heightInches: string;
    weight: string;
    hasUsedNicotine: boolean;
    hasMedicalConditions: boolean | null;
    hasDiagnosticTesting: boolean | null;
}

export interface StatementOfHealthRef extends BaseRef {
    getValues: () => StatementOfHealthValues;
}

const BaseContactInformationSchema = z.object({
    applicantType: ApplicantTypeEnum,
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    gender: z
        .enum(["male", "female"], {
            errorMap: () => ({ message: "Please select your gender" }),
        })
        .optional(),
    email: emailSchema,
    phone: phoneSchema,
    phoneType: phoneTypeSchema,
    tcpaConsent: z.boolean().optional(),
    address: addressSchema,
    city: citySchema,
    state: stateSchema,
    zipCode: zipCodeSchema,
});

export const DTOContactInformationSchema = BaseContactInformationSchema.extend({
    dateOfBirth: dtoDateOfBirthSchema,
    aaaMemberNumber: aaaMemberNumberSchema.optional(),
});

export const MLTAContactInformationSchema = BaseContactInformationSchema.extend({
    dateOfBirth: mltaDateOfBirthSchema,
    aaaMemberNumber: aaaMemberNumberSchema.optional(),
});

export const GIWLContactInformationSchema = BaseContactInformationSchema.extend({
    dateOfBirth: giwlDateOfBirthSchema,
    aaaMemberNumber: aaaMemberNumberSchema.optional(),
});

export interface ContactInformationValues {
    applicantType: "member" | "spouse";
    firstName: string;
    lastName: string;
    dateOfBirth: {
        month: string;
        day: string;
        year: string;
    };
    gender?: "male" | "female";
    email: string;
    phone: string;
    phoneType: "Home" | "Mobile" | "Business";
    tcpaConsent?: boolean;
    address: string;
    city: string;
    state: UsStates;
    zipCode: string;
    aaaMemberNumber?: string; // MLTA/GIWL - required when offerData.aaaMemberNumber is not available
}

export interface ContactInformationRef extends BaseRef {
    getValues: () => ContactInformationValues;
}

export const PolicyBeneficiarySchema = z.object({
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    relationship: z.string().min(1, { message: VALIDATION_MESSAGES_CARD.SELECT_OPTION }),
});

export interface PolicyBeneficiaryValues {
    firstName: string;
    lastName: string;
    relationship: BeneficiaryRelationshipsEnum;
}

export interface PolicyBeneficiaryRef extends BaseRef {
    getValues: () => PolicyBeneficiaryValues;
}

export const SecondaryAddresseeSchema = z
    .object({
        hasSecondaryAddressee: z
            .boolean()
            .nullable()
            .refine((val) => val !== null, {
                message: VALIDATION_MESSAGES_CARD.SELECT_OPTION,
            }),
        firstName: optionalFirstNameSchema,
        lastName: optionalLastNameSchema,
        address: optionalAddressSchema,
        city: optionalCitySchema,
        state: optionalStateSchema,
        zipCode: optionalZipCodeSchema,
        phone: optionalPhoneSchema,
    })
    .refine(
        (data) => {
            // Only validate if hasSecondaryAddressee is true
            if (data.hasSecondaryAddressee === true) {
                return data.firstName && data.firstName.trim().length > 0;
            }
            return true;
        },
        {
            message: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED,
            path: ["firstName"],
        }
    )
    .refine(
        (data) => {
            return !(data.hasSecondaryAddressee === true && (!data.lastName || data.lastName.trim().length === 0));
        },
        {
            message: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED,
            path: ["lastName"],
        }
    )
    .refine(
        (data) => {
            return !(data.hasSecondaryAddressee === true && (!data.address || data.address.trim().length === 0));
        },
        {
            message: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED,
            path: ["address"],
        }
    )
    .refine(
        (data) => {
            return !(data.hasSecondaryAddressee === true && (!data.city || data.city.trim().length === 0));
        },
        {
            message: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED,
            path: ["city"],
        }
    )
    .refine(
        (data) => {
            return !(data.hasSecondaryAddressee === true && (!data.state || data.state.trim().length === 0));
        },
        {
            message: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED,
            path: ["state"],
        }
    )
    .refine(
        (data) => {
            return !(data.hasSecondaryAddressee === true && (!data.zipCode || data.zipCode.trim().length === 0));
        },
        {
            message: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED,
            path: ["zipCode"],
        }
    )
    .refine(
        (data) => {
            return !(data.hasSecondaryAddressee === true && (!data.phone || data.phone.trim().length === 0));
        },
        {
            message: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED,
            path: ["phone"],
        }
    );

export interface SecondaryAddresseeValues {
    hasSecondaryAddressee: boolean | null;
    firstName?: string;
    lastName?: string;
    address?: string;
    city?: string;
    state?: UsStates;
    zipCode?: string;
    phone?: string;
}

export interface SecondaryAddresseeRef extends BaseRef {
    getValues: () => SecondaryAddresseeValues;
}

// Quote Card Schema and Types
const BaseQuoteSchema = z.object({
    state: stateSchema,
    coverageAmount: z.number().positive(),
    coveragePremium: z.number().positive(),
});

export const DTOQuoteSchema = BaseQuoteSchema.extend({
    dateOfBirth: dtoDateOfBirthSchema,
    applicantType: ApplicantTypeEnum,
    gender: genderSchema,
    nicotineUsage: z.string().min(1, { message: "This field is required" }),
});

export const MLTAQuoteSchema = BaseQuoteSchema.extend({
    dateOfBirth: mltaDateOfBirthSchema,
    zipCode: zipCodeSchema,
});

export const GIWLQuoteSchema = BaseQuoteSchema.extend({
    dateOfBirth: giwlDateOfBirthSchema,
    zipCode: zipCodeSchema,
});

// Centralized GIWL landing-page hydration shape, used by the URL parser (utils/giwlQuoteParams).
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const GIWLQuoteParamsSchema = z
    .object({
        gender: genderSchema,
        dob: z.string().min(1, { message: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED }),
        zipCode: zipCodeSchema,
        // Nullable: when absent the state is resolved from the ZIP code downstream.
        stateFromParams: giwlStateSchema.nullable(),
        coverage: z.string().min(1, { message: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED }),
        premium: z.string().min(1, { message: VALIDATION_MESSAGES_CARD.FIELD_REQUIRED }),
        email: z.string().optional(),
        isMember: z.boolean(),
        policyType: z.string().optional(),
    })
    .superRefine((params, ctx) => {
        // dob arrives as an ISO string; validate it against the {month,day,year} age schema by splitting.
        if (!ISO_DATE_REGEX.test(params.dob)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Please enter a valid date of birth",
                path: ["dob"],
            });
            return;
        }
        const [year, month, day] = params.dob.split("-");
        if (!giwlDateOfBirthSchema.safeParse({ month, day, year }).success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Please enter a valid date of birth",
                path: ["dob"],
            });
        }
    });

export type ParsedGIWLQuoteParams = z.infer<typeof GIWLQuoteParamsSchema>;

const BaseApplicationFormSchema = z.object({
    policyBeneficiary: PolicyBeneficiarySchema,
    secondaryAddressee: SecondaryAddresseeSchema,
});

export const DTOApplicationFormSchema = BaseApplicationFormSchema.extend({
    contactInformation: DTOContactInformationSchema,
    coverageReplacement: CoverageReplacementSchema,
    statementOfHealth: StatementOfHealthSchema,
    quote: DTOQuoteSchema,
});

export const MLTAApplicationFormSchema = BaseApplicationFormSchema.extend({
    contactInformation: MLTAContactInformationSchema,
    quote: MLTAQuoteSchema,
});

export const GIWLApplicationFormSchema = BaseApplicationFormSchema.extend({
    contactInformation: GIWLContactInformationSchema,
    coverageReplacement: CoverageReplacementSchema,
    quote: GIWLQuoteSchema,
});

export type DTOApplicationFormData = z.infer<typeof DTOApplicationFormSchema>;
export type MLTAApplicationFormData = z.infer<typeof MLTAApplicationFormSchema>;
export type GIWLApplicationFormData = z.infer<typeof GIWLApplicationFormSchema>;

export type DTOApplicationFormReturn = UseFormReturn<DTOApplicationFormData>;
export type MLTAApplicationFormReturn = UseFormReturn<MLTAApplicationFormData>;

// Shared component form types
// These components accept either DTO or MLTA forms
export type ContactInformationFormReturn = DTOApplicationFormReturn | MLTAApplicationFormReturn;
export type PolicyBeneficiaryFormReturn = DTOApplicationFormReturn | MLTAApplicationFormReturn;
export type SecondaryAddresseeFormReturn = DTOApplicationFormReturn | MLTAApplicationFormReturn;

export interface QuoteValues {
    applicantType: "member" | "spouse";
    dateOfBirth: {
        month: string;
        day: string;
        year: string;
    };
    gender: "male" | "female";
    state: UsStates;
    nicotineUsage: string;
    coverageAmount: number;
    coveragePremium: number;
    zipCode?: string; // Optional - used by MLTA only
}

export interface QuoteRef extends BaseRef {
    getValues: () => QuoteValues;
}
